import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/auth';
import { InlineLoader } from '../../components/ui/PremiumLoader';
import { useAnalytics, AnalyticsEvents } from '../../hooks/useAnalytics';
import { GoogleRoleModal } from '../../components/GoogleRoleModal';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    login, 
    loginWithGoogle, 
    completeGoogleRegistration,
    clearPendingGoogleRegistration,
    pendingGoogleRegistration,
    isLoading 
  } = useAuthStore();
  const { trackEvent } = useAnalytics();
  const [showPassword, setShowPassword] = useState(false);
  const [isGoogleRegistering, setIsGoogleRegistering] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      await login(data.email, data.password);
      // Track successful login (no PII)
      trackEvent(AnalyticsEvents.USER_SIGNED_IN, {
        method: 'email',
      });
      navigate(from, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      if (message === 'EMAIL_NOT_VERIFIED') {
        toast.error('Please verify your email before logging in');
        navigate('/verify-email');
      } else {
        toast.error(message);
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await loginWithGoogle();
      // Track successful Google login (no PII)
      trackEvent(AnalyticsEvents.USER_SIGNED_IN, {
        method: 'google',
      });
      navigate(from, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Google login failed';
      if (message === 'GOOGLE_REGISTRATION_REQUIRED') {
        // Modal will be shown automatically since pendingGoogleRegistration is set
        // No toast needed - user will see the modal
      } else if (message === 'REGISTRATION_REQUIRED') {
        toast.error('Please register first');
        navigate('/register');
      } else if (message === 'EMAIL_NOT_VERIFIED') {
        toast.error('Please verify your email before logging in');
        navigate('/verify-email');
      } else {
        toast.error(message);
      }
    }
  };

  const handleGoogleRoleComplete = async (role: 'STUDENT' | 'PROFESSOR') => {
    setIsGoogleRegistering(true);
    try {
      await completeGoogleRegistration(role);
      // Track successful Google registration (no PII)
      trackEvent(AnalyticsEvents.USER_SIGNED_UP, {
        method: 'google',
        role,
      });
      navigate(from, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      toast.error(message);
    } finally {
      setIsGoogleRegistering(false);
    }
  };

  const handleGoogleModalClose = () => {
    clearPendingGoogleRegistration();
  };

  return (
    <motion.div
      initial="initial"
      animate="animate"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div variants={fadeUp} transition={{ duration: 0.4 }}>
        <h1 className="text-3xl font-bold text-white">
          Welcome Back
        </h1>
        <p className="mt-2 text-white/50">
          Sign in to continue your learning journey
        </p>
      </motion.div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Email - masked from analytics */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.05 }} className="ph-no-capture">
          <label htmlFor="email" className="label">Email</label>
          <div className="relative group">
            <input
              id="email"
              type="email"
              {...register('email')}
              className={`input pl-11 ${errors.email ? 'input-error' : ''}`}
              placeholder="you@example.com"
              data-ph-no-capture
            />
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 
                           group-focus-within:text-slate-light transition-colors" />
          </div>
          {errors.email && (
            <p className="mt-1.5 text-sm text-coral">{errors.email.message}</p>
          )}
        </motion.div>

        {/* Password - masked from analytics */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.1 }} className="ph-no-capture">
          <label htmlFor="password" className="label">Password</label>
          <div className="relative group">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              {...register('password')}
              className={`input pl-11 pr-11 ${errors.password ? 'input-error' : ''}`}
              placeholder="••••••••"
              data-ph-no-capture
            />
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30
                           group-focus-within:text-slate-light transition-colors" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 
                       hover:text-white/60 transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-sm text-coral">{errors.password.message}</p>
          )}
        </motion.div>

        {/* Forgot password */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.15 }} className="flex justify-end">
          <Link to="/forgot-password" className="text-sm text-slate-light hover:text-slate-lighter transition-colors font-medium">
            Forgot password?
          </Link>
        </motion.div>

        {/* Submit */}
        <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.2 }}>
          <button type="submit" disabled={isLoading} className="btn-primary w-full group">
            {isLoading ? (
              <>
                <InlineLoader className="mr-2" />
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <span>Sign In</span>
                <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
        </motion.div>
      </form>

      {/* Divider */}
      <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.25 }} className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-white/10" />
        </div>
        <div className="relative flex justify-center">
          <span className="px-4 bg-void text-white/30 text-sm">Or continue with</span>
        </div>
      </motion.div>

      {/* Google login - Colorful */}
      <motion.div variants={fadeUp} transition={{ duration: 0.4, delay: 0.3 }}>
        <button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="btn-google"
        >
          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Continue with Google
        </button>
      </motion.div>

      {/* Register link */}
      <motion.p variants={fadeUp} transition={{ duration: 0.4, delay: 0.35 }} className="text-center text-white/50">
        Don't have an account?{' '}
        <Link to="/register" className="text-slate-light hover:text-slate-lighter font-medium transition-colors">
          Sign up
        </Link>
      </motion.p>

      {/* Google Role Selection Modal */}
      <GoogleRoleModal
        isOpen={!!pendingGoogleRegistration}
        onClose={handleGoogleModalClose}
        onComplete={handleGoogleRoleComplete}
        userData={pendingGoogleRegistration}
        isLoading={isGoogleRegistering}
      />
    </motion.div>
  );
}
